import DocumentSchema from '@components/DocumentSchema';
import type { Table } from 'schema-dts';
import {
  EarningsForecastDataTable,
  TableDataCell,
  TableWrapper,
  FutureEstimateRow,
} from './styled';
import { getFutureRevenueGrowthRawDataProps } from '../../redux/selectors';
import { useSelector, shallowEqual } from 'react-redux';
import { useMemo } from 'react';
import { separateByThreeDigit } from '@simplywallst/formatting';
import ListTruncator from '@/components/ListTruncator';
import { useSummary } from '@/hooks/useCompany';

const EarningsAndRevenueGrowthTable = () => {
  const summary = useSummary();
  const companyId = summary.status === 'success' ? summary.companyId : '';
  const uniqueSymbol = summary.status === 'success' ? summary.uniqueSymbol : '';
  const { futureEstimates, pastFinancialData, viewMode } = useSelector(
    (state) => getFutureRevenueGrowthRawDataProps(state, { companyId }),
    shallowEqual
  );
  const combinedData = useMemo(
    () => futureEstimates.concat(pastFinancialData),
    [futureEstimates, pastFinancialData]
  );
  const caption = `${uniqueSymbol} - Analysts future estimates and past financials data (USD Millions)`;

  const table = (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: 'Earnings and Revenue Growth Forecast',
          about: caption,
        }}
      />
      <TableWrapper>
        <EarningsForecastDataTable data-cy-id="earnings-and-revenue-growth-table">
          <caption>{caption}</caption>
          <thead>
            <tr>
              <th>Date</th>
              <th>Revenue</th>
              <th>Earnings</th>
              <th>Free Cash Flow</th>
              <th>Cash from Op</th>
              <th>Avg. No. Analysts</th>
            </tr>
          </thead>
          <tbody>
            {futureEstimates.map(({ data, dateKey }, i) => {
              return (
                <FutureEstimateRow
                  key={i}
                  data-cy-id={`earnings-and-revenue-growth-row-${i}`}
                >
                  <td>{new Date(dateKey).toLocaleDateString()}</td>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <TableDataCell key={i} darken={!data[i]}>
                      {data[i] ? separateByThreeDigit(data[i], 0) : 'N/A'}
                    </TableDataCell>
                  ))}
                </FutureEstimateRow>
              );
            })}
            {pastFinancialData.map(({ data, dateKey }, i) => {
              return (
                <tr key={i} data-cy-id={`earnings-and-revenue-growth-row-${i}`}>
                  <td>{new Date(dateKey).toLocaleDateString()}</td>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <TableDataCell key={i} darken={!data[i]}>
                      {data[i] ? separateByThreeDigit(data[i], 0) : 'N/A'}
                    </TableDataCell>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </EarningsForecastDataTable>
      </TableWrapper>
    </>
  );

  return combinedData.length > 9 ? (
    <div data-cy-id="earnings-and-revenue-growth-table-truncator">
      <ListTruncator viewMode={viewMode} minHeight={400}>
        {table}
      </ListTruncator>
    </div>
  ) : (
    table
  );
};
export default EarningsAndRevenueGrowthTable;
